// FloorPlanItemsPanel.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import {
  FLOOR_PLAN_ITEM_DRAG_MIME,
  groupFloorPlanItems,
} from 'nodetypes/project/floorplan/floorPlanItemPlacement';
import { getPurchaseOrderItemRowModel } from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';

// CSS
import './FloorPlanItemsPanel.css';

/**
 * FloorPlanItemCover
 *
 * Resolves and shows a PO item cover above the title when set.
 *
 * @param {object} props
 * @param {string} props.itemKey
 * @param {string} props.coverUrl
 * @returns {ReactNode|null}
 */
const FloorPlanItemCover = React.memo(({ itemKey, coverUrl }) => {
  // ----- STATE ----- //

  // Resolved image src (signed URL when needed)
  const [src, setSrc] = useState('');

  // ----- EFFECTS ----- //

  // Resolve storage paths to signed URLs; pass through http(s)/blob/data.
  useEffect(() => {
    let cancelled = false;
    const raw = String(coverUrl || '').trim();
    if (!raw) {
      setSrc('');
      return undefined;
    }
    if (
      raw.startsWith('blob:')
      || raw.startsWith('data:')
      || raw.startsWith('http://')
      || raw.startsWith('https://')
      || raw.startsWith('/')
    ) {
      setSrc(raw);
      return undefined;
    }
    (async () => {
      try {
        const signed = await resolveImageUrl({ nodeKey: itemKey || null, url: raw });
        if (!cancelled) setSrc(signed || raw);
      } catch {
        if (!cancelled) setSrc(raw);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coverUrl, itemKey]);

  if (!src) return null;

  // ----- RENDER ----- //

  return (
    <div className="floor-plan-items-panel-item-cover" aria-hidden="true">
      <img
        className="floor-plan-items-panel-item-cover-img"
        src={src}
        alt=""
        draggable={false}
        loading="lazy"
      />
    </div>
  );
});

FloorPlanItemCover.displayName = 'FloorPlanItemCover';

/**
 * FloorPlanItemsPanel
 *
 * Side list of PO items. Use variant "unassigned" (left) or "rooms" (right).
 *
 * @param {'unassigned'|'rooms'} props.variant
 * @param {object[]} props.items
 * @param {object|null} props.layout
 * @param {string|null} [props.activeFloorId]
 * @param {boolean} props.isEditing
 * @param {boolean} [props.isLoading=false]
 * @param {Function} [props.onUnassignDrop] - (itemKey) => void when dropping onto unassigned
 * @param {Function} [props.onRoomDrop] - (itemKey, room) => void when dropping onto a room group
 * @param {Function} [props.onItemClick] - (item) => void; skipped after a drag
 * @returns {ReactNode}
 */
const FloorPlanItemsPanel = React.memo(({
  variant = 'unassigned',
  items = [],
  layout = null,
  activeFloorId = null,
  isEditing = false,
  isLoading = false,
  onUnassignDrop = null,
  onRoomDrop = null,
  onItemClick = null,
}) => {
  // ----- STATE ----- //

  // Room group currently targeted by a drag-over
  const [dropRoomId, setDropRoomId] = useState(null);

  // ----- REFS ----- //

  // Suppress the click that fires after HTML5 drag-and-drop ends.
  const suppressClickRef = useRef(false);

  // ----- MEMOS ----- //

  // Group items into unassigned + room buckets.
  const { unassigned, groups } = useMemo(
    () => groupFloorPlanItems(items, layout),
    [items, layout],
  );

  // Rooms on the active floor (empty rooms still listed).
  const activeFloorRooms = useMemo(() => {
    const floors = Array.isArray(layout?.floors) ? layout.floors : [];
    const floor = floors.find((row) => row.id === activeFloorId) || floors[0] || null;
    return Array.isArray(floor?.rooms) ? floor.rooms : [];
  }, [activeFloorId, layout]);

  // Lookup map of items by room key.
  const itemsByRoomKey = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => {
      map.set(group.roomKey, group.items);
    });
    return map;
  }, [groups]);

  // ----- HANDLERS ----- //

  /**
   * handleDragStart
   *
   * @param {DragEvent} event
   * @param {object} item
   */
  const handleDragStart = (event, item) => {
    if (!isEditing || !item?.key) return;
    suppressClickRef.current = true;
    event.dataTransfer.setData(FLOOR_PLAN_ITEM_DRAG_MIME, item.key);
    event.dataTransfer.setData('text/plain', item.key);
    event.dataTransfer.effectAllowed = 'move';
  };

  /**
   * handleDragEnd
   */
  const handleDragEnd = () => {
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  /**
   * handleItemClick
   *
   * @param {object} item
   */
  const handleItemClick = (item) => {
    if (suppressClickRef.current) return;
    if (typeof onItemClick !== 'function' || !item?.key) return;
    onItemClick(item);
  };

  /**
   * handleUnassignDragOver
   *
   * @param {DragEvent} event
   */
  const handleUnassignDragOver = (event) => {
    if (!isEditing || typeof onUnassignDrop !== 'function') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  /**
   * handleUnassignDrop
   *
   * @param {DragEvent} event
   */
  const handleUnassignDrop = (event) => {
    if (!isEditing || typeof onUnassignDrop !== 'function') return;
    event.preventDefault();
    const itemKey = String(
      event.dataTransfer.getData(FLOOR_PLAN_ITEM_DRAG_MIME)
      || event.dataTransfer.getData('text/plain')
      || '',
    ).trim();
    if (!itemKey) return;
    onUnassignDrop(itemKey);
  };

  /**
   * handleRoomDragOver
   *
   * @param {DragEvent} event
   * @param {string} roomId
   */
  const handleRoomDragOver = (event, roomId) => {
    if (!isEditing || typeof onRoomDrop !== 'function') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dropRoomId !== roomId) setDropRoomId(roomId);
  };

  /**
   * handleRoomDragLeave
   *
   * @param {DragEvent} event
   * @param {string} roomId
   */
  const handleRoomDragLeave = (event, roomId) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    if (dropRoomId === roomId) setDropRoomId(null);
  };

  /**
   * handleRoomDrop
   *
   * @param {DragEvent} event
   * @param {object} room
   */
  const handleRoomDrop = (event, room) => {
    if (!isEditing || typeof onRoomDrop !== 'function' || !room?.id) return;
    event.preventDefault();
    event.stopPropagation();
    setDropRoomId(null);
    const itemKey = String(
      event.dataTransfer.getData(FLOOR_PLAN_ITEM_DRAG_MIME)
      || event.dataTransfer.getData('text/plain')
      || '',
    ).trim();
    if (!itemKey) return;
    onRoomDrop(itemKey, room);
  };

  /**
   * renderItem
   *
   * @param {object} item
   * @returns {ReactNode}
   */
  const renderItem = (item) => {
    const model = getPurchaseOrderItemRowModel(item);
    const metaParts = [
      model.quantity != null ? `Qty ${model.quantity}` : null,
      model.sku || null,
    ].filter(Boolean);

    return (
      <div
        key={item.key}
        className={[
          'floor-plan-items-panel-item',
          isEditing ? null : 'is-readonly',
          typeof onItemClick === 'function' ? 'is-clickable' : null,
        ].filter(Boolean).join(' ')}
        draggable={isEditing}
        role={typeof onItemClick === 'function' ? 'button' : undefined}
        tabIndex={typeof onItemClick === 'function' ? 0 : undefined}
        title={isEditing ? 'Drag onto a room, or click to open' : model.title}
        onClick={() => handleItemClick(item)}
        onDragEnd={handleDragEnd}
        onDragStart={(event) => handleDragStart(event, item)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          handleItemClick(item);
        }}
      >
        {/* ITEM COVER */}
        {model.coverUrl ? (
          <FloorPlanItemCover itemKey={model.key || item.key} coverUrl={model.coverUrl} />
        ) : null}

        {/* ITEM TITLE */}
        <div className="floor-plan-items-panel-item-title">
          {model.title || 'Untitled item'}
        </div>

        {/* ITEM META */}
        {metaParts.length ? (
          <div className="floor-plan-items-panel-item-meta">
            {metaParts.join(' · ')}
          </div>
        ) : null}
      </div>
    );
  };

  // ----- RENDER ----- //

  if (variant === 'rooms') {
    return (
      <div className="floor-plan-items-panel-container is-rooms">

        {/* HEADER */}
        <div className="floor-plan-items-panel-header">
          <div className="floor-plan-items-panel-title">Rooms</div>
          <div className="floor-plan-items-panel-count">{activeFloorRooms.length}</div>
        </div>

        {/* BODY */}
        <div className="floor-plan-items-panel-body">
          {isLoading ? (
            <div className="floor-plan-items-panel-empty">Loading…</div>
          ) : null}

          {!isLoading && activeFloorRooms.length === 0 ? (
            <div className="floor-plan-items-panel-empty">
              No rooms on this floor yet.
            </div>
          ) : null}

          {!isLoading && activeFloorRooms.map((room) => {
            const roomItems = itemsByRoomKey.get(room.id) || [];
            return (

              /* GROUP */
              <div
                key={room.id}
                className={[
                  'floor-plan-items-panel-group',
                  dropRoomId === room.id ? 'is-drop-target' : null,
                ].filter(Boolean).join(' ')}
                onDragLeave={(event) => handleRoomDragLeave(event, room.id)}
                onDragOver={(event) => handleRoomDragOver(event, room.id)}
                onDrop={(event) => handleRoomDrop(event, room)}
              >

                {/* GROUP LABEL */}
                <div className="floor-plan-items-panel-group-label">
                  {room.name}
                  <span className="floor-plan-items-panel-group-count">
                    {roomItems.length}
                  </span>
                </div>
                {roomItems.length
                  ? roomItems.map(renderItem)
                  : (
                    <div className="floor-plan-items-panel-empty is-compact">
                      {isEditing ? 'Drop items here' : 'Empty'}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className="floor-plan-items-panel-container is-unassigned"
      onDragOver={handleUnassignDragOver}
      onDrop={handleUnassignDrop}
    >

      {/* HEADER */}
      <div className="floor-plan-items-panel-header">
        <div className="floor-plan-items-panel-title">Unassigned</div>
        <div className="floor-plan-items-panel-count">{unassigned.length}</div>
      </div>

      {/* HINT 
      {isEditing ? (
        <div className="floor-plan-items-panel-hint">
          Drag onto a room on the plan or in Rooms.
        </div>
      ) : null}
       */}

      {/* BODY */}
      <div className="floor-plan-items-panel-body">
        {isLoading ? (
          <div className="floor-plan-items-panel-empty">Loading items…</div>
        ) : null}

        {!isLoading && items.length === 0 ? (
          <div className="floor-plan-items-panel-empty">
            No purchase order items on this project yet.
          </div>
        ) : null}

        {!isLoading && items.length > 0 && unassigned.length === 0 ? (
          <div className="floor-plan-items-panel-empty">
            All items are placed.
          </div>
        ) : null}

        {!isLoading ? unassigned.map(renderItem) : null}
      </div>
    </div>
  );
});

// Dev Tools
FloorPlanItemsPanel.displayName = 'FloorPlanItemsPanel';

export default FloorPlanItemsPanel;
